import React, {useState} from 'react';
import {
    Card,
    Form,
    Input,
    Button,
    Space,
    Typography,
    Layout,
    Row,
    Col,
    Modal
} from 'antd';
import {PrinterOutlined} from '@ant-design/icons';
import PdfViewer from '../../components/shared/PdfViewer';
import {reportService} from "../../services/domain/report/service";
import RocDateInputPicker from "../../components/shared/RocDateInputPicker";
import dayjs from "dayjs";

const {Title} = Typography;
const {Content} = Layout;

const PriceCertificatePage: React.FC = () => {
    const [form] = Form.useForm();
    // 新增PDF預覽狀態
    const [pdfPreviewVisible, setPdfPreviewVisible] = useState(false);

    // 重置表單
    const handleReset = () => {
        form.resetFields();
    };

    // 確認按鈕點擊處理函數
    const handleConfirm = () => {
        // 表單驗證
        form.validateFields()
            .then(values => {
                // 彈出詢問視窗
                Modal.confirm({
                    title: '確認',
                    content: '是否預覽列印？',
                    okText: '是',
                    cancelText: '否',
                    onOk: () => {
                        // 使用者選擇「是」，顯示PDF預覽
                        setPdfPreviewVisible(true);
                    },
                    onCancel: () => {
                        // 使用者選擇「否」，只關閉詢問視窗，不執行其他操作
                        console.log('使用者選擇不預覽列印');
                    }
                });
            })
            .catch(error => {
                console.error('Validation failed:', error);
            });
    };

    // 關閉PDF預覽視窗
    const handleClosePdfPreview = () => {
        setPdfPreviewVisible(false);
    };

    const handleLoadPdfData = async () => {
        const response = await reportService.getMembershipParityProvePdf({
            memberId: form.getFieldValue('memberId') ?? '',
            year: form.getFieldValue('year') ?? '',
            serialNo: form.getFieldValue('serialNo') ?? '',
            expDate: form.getFieldValue('expDate')?.format('YYYY-MM-DD') ?? '',
            org: form.getFieldValue('org') ?? '',
            issueDate: form.getFieldValue('issueDate')?.format('YYYY-MM-DD') ?? '',
        });
        return response;
    };

    /**
     * Export PDF
     */
    const exportPdf = async (filename: string) => {
        await reportService.exportMembershipParityProvePdf({
            memberId: form.getFieldValue('memberId') ?? '',
            year: form.getFieldValue('year') ?? '',
            serialNo: form.getFieldValue('serialNo') ?? '',
            expDate: form.getFieldValue('expDate')?.format('YYYY-MM-DD') ?? '',
            org: form.getFieldValue('org') ?? '',
            issueDate: form.getFieldValue('issueDate')?.format('YYYY-MM-DD') ?? '',
        });
    };

    /**
     * Export Excel
     */
    const exportExcel = async (filename: string) => {
        await reportService.exportMembershipParityProveExcel({
            memberId: form.getFieldValue('memberId') ?? '',
            year: form.getFieldValue('year') ?? '',
            serialNo: form.getFieldValue('serialNo') ?? '',
            expDate: form.getFieldValue('expDate')?.format('YYYY-MM-DD') ?? '',
            org: form.getFieldValue('org') ?? '',
            issueDate: form.getFieldValue('issueDate')?.format('YYYY-MM-DD') ?? '',
        });
    };


    // 依據螢幕尺寸提供不同的標籤和輸入欄位布局
    const formItemLayout = {
        labelCol: {
            xs: {span: 24}, // 在極小螢幕下，標籤佔滿整行
            sm: {span: 24}, // 在小螢幕下，標籤佔滿整行
            md: {span: 8}, // 在中等螢幕下，標籤佔8格
            lg: {span: 6}, // 在大螢幕下，標籤佔6格
        },
        wrapperCol: {
            xs: {span: 24}, // 在極小螢幕下，輸入欄位佔滿整行
            sm: {span: 24}, // 在小螢幕下，輸入欄位佔滿整行
            md: {span: 16}, // 在中等螢幕下，輸入欄位佔16格
            lg: {span: 18}, // 在大螢幕下，輸入欄位佔18格
        },
    };

    return (
        <Layout className="price-certificate-page">
            <Content>
                {/* Page Title */}
                <Row justify="center" style={{marginBottom: 24}}>
                    <Col>
                        <Title level={3}>比價證明書</Title>
                    </Col>
                </Row>

                {/* Main Card */}
                <Card>
                    <Form
                        form={form}
                        {...formItemLayout}
                        // layout="vertical"
                        initialValues={{
                            org: '經濟部',
                            issueDate: dayjs()
                        }}
                        style={{maxWidth: 800, margin: '0 auto'}}
                    >
                        <Row gutter={24}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="會員證號碼"
                                    name="memberId"
                                    rules={[{required: true, message: '請輸入會員證號碼'}]}
                                >
                                    <Input placeholder="請輸入會員證號碼"/>
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item label="字號" className="range-field">
                                    <Space.Compact>
                                        <Form.Item noStyle name="year">
                                            <Input placeholder="證明書年份"/>
                                        </Form.Item>
                                        <Form.Item noStyle name="serialNo">
                                            <Input placeholder="證明書字號"/>
                                        </Form.Item>
                                    </Space.Compact>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={24}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="有效日期"
                                    name="expDate"
                                >
                                    <RocDateInputPicker />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="發給日期"
                                    name="issueDate"
                                >
                                    <RocDateInputPicker />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={24}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="發證機關"
                                    name="org"
                                >
                                    <Input placeholder="請輸入發證機關"/>
                                </Form.Item>
                            </Col>

                        </Row>

                        {/* Action Buttons */}
                        <Row justify="center" style={{marginTop: 24}}>
                            <Space>
                                <Button
                                    type="primary"
                                    icon={<PrinterOutlined/>}
                                    onClick={handleConfirm}
                                >
                                    確認
                                </Button>
                                <Button
                                    type="default"
                                    onClick={handleReset}
                                >
                                    重置
                                </Button>
                            </Space>
                        </Row>
                    </Form>
                </Card>

                {/* PDF預覽視窗 */}
                {
                    pdfPreviewVisible && (
                        <PdfViewer
                            visible={pdfPreviewVisible}
                            onClose={handleClosePdfPreview}
                            title="比價證明書預覽"
                            loadPdfData={handleLoadPdfData}
                            exportPdf={exportPdf}
                            exportExcel={exportExcel}
                        />
                    )
                }
            </Content>
        </Layout>
    );
};

export default PriceCertificatePage;